const GROQ_MODEL = "llama-3.3-70b-versatile";

const RELATIONSHIP_KNOWLEDGE = `You are an expert in relationship frameworks and dynamics. You analyze how two specific people relate to each other based on their independent answers about their shared connection.

Both people answered the same five light, associative questions about each other — independently, without seeing the other's answers. Your job is to read both sides and find what they reveal about the nature of this connection.

You use the following knowledge about relationship types:
- Monogamy: emotional and sexual exclusivity, deep interdependence, long-term commitment
- Open relationship: committed primary partnership with agreed freedom for outside connections
- Polyamory: multiple loving relationships with full knowledge and consent of all involved
- Relationship anarchy: no predetermined hierarchy or rules; each connection defined on its own terms
- Friends with benefits: close friendship with sexual or intimate connection, without romantic commitment
- Queerplatonic relationship: a deeply committed non-romantic relationship that transcends typical friendship
- Comet relationship: deep but infrequent connection; people who drift apart and reconnect with full intensity
- Anchor partnership: a stable, grounding relationship that isn't necessarily romantic or sexual
- Chosen family: a non-biological bond with the depth and commitment of family
- Situationship: an undefined romantic or sexual connection without agreed labels
- Conscious relationship: both parties actively discuss and renegotiate their connection

When analyzing:
- Look for alignment: where do their answers echo each other?
- Look for asymmetry: does one person seem to feel the connection differently or more intensely?
- Look for texture: what kind of energy does this connection have — playful, grounding, intense, easy?
- Keep the tone warm and curious, not clinical.

Always write in fluent English. No markdown, no bullet points.`;

export function buildPrompt(p1Answers, p2Answers, p1Name, p2Name, questions) {
  const p1 = p1Answers.map((a, i) =>
    `Question ${i + 1}: "${questions[i].text}"\n${p1Name}: ${a}`
  ).join("\n\n");
  const p2 = p2Answers.map((a, i) =>
    `Question ${i + 1}: "${questions[i].text}"\n${p2Name}: ${a}`
  ).join("\n\n");

  return `Here are ${p1Name} and ${p2Name}'s independent answers about their connection.

${p1}

---

${p2}

Respond using EXACTLY this format. Write ONLY the content after each tag — no repeating the instruction, no preamble:

[LABEL]
The single relationship framework that best fits this connection (2-5 words max, e.g. "Queerplatonic relationship" or "Conscious friendship").

[OVERALL]
A warm, flowing paragraph synthesising this connection between ${p1Name} and ${p2Name}. What patterns emerge? What's the energy? Why does the label above fit? End with one sentence about what this analysis can't capture.

[Q1]
Compare ${p1Name} and ${p2Name}'s answers to question 1 in 2-3 sentences. Only the comparison — no intro, no repeating the question.

[Q2]
Same for question 2.

[Q3]
Same for question 3.

[Q4]
Same for question 4.

[Q5]
Same for question 5.`;
}

export async function runGroqAnalysis({ groqKey, p1Answers, p2Answers, p1Name, p2Name, questions, onToken }) {
  const resp = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${groqKey.trim()}`
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      messages: [
        { role: "system", content: RELATIONSHIP_KNOWLEDGE },
        { role: "user", content: buildPrompt(p1Answers, p2Answers, p1Name, p2Name, questions) }
      ],
      stream: true,
      temperature: 0.7,
      max_tokens: 2000
    })
  });

  if (!resp.ok) {
    const errData = await resp.json();
    throw new Error(errData?.error?.message || `Status ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let fullText = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    const lines = chunk.split("\n").filter(l => l.startsWith("data: "));
    for (const line of lines) {
      const data = line.replace("data: ", "");
      if (data === "[DONE]") continue;
      try {
        const json = JSON.parse(data);
        const token = json?.choices?.[0]?.delta?.content || "";
        fullText += token;
        onToken(fullText);
      } catch {}
    }
  }

  return fullText;
}